import {
  AUDIT_ENTITY_UNIT_MAIN_STATE_TYPE_ID_LOOKUP,
  AUDIT_ENTITY_UNIT_STATUS_LOOKUP,
  AUDIT_ENTITY_UNIT_THEME_ID_LOOKUP,
  AUDIT_ENTITY_UNIT_TYPE_ID_LOOKUP,
  ENTITY_UNITS
} from '../constants';

// Audit Entity Utils
export const resolveUnitsIntlLookUp = (intl: any, value: string, i18nLookup: string): string => {
  switch (i18nLookup) {
    case AUDIT_ENTITY_UNIT_MAIN_STATE_TYPE_ID_LOOKUP:
      return intl.formatMessage({ id: `units.mainStateId.${value}` });
    case AUDIT_ENTITY_UNIT_STATUS_LOOKUP:
      return intl.formatMessage({ id: `trackingUnits.unitStatus${value}` });
    case AUDIT_ENTITY_UNIT_THEME_ID_LOOKUP:
      return intl.formatMessage({ id: `units.theme${value}` });
    case AUDIT_ENTITY_UNIT_TYPE_ID_LOOKUP:
      return intl.formatMessage({ id: `trackingUnits.unitType${value}` });
    default:
      return '';
  }
};

/*
 * Formateo unificado de una entrada de bitácora (WD-1170).
 * Centralizado aquí para que todos los renderers (SlidingPaneEntityBinnacle,
 * SlidingPaneEntityTimelineBinnacle y el panel del mapa) se comporten igual.
 *  - Entradas que no son de auditoría (comentarios/eventos) -> texto crudo.
 *  - Cambios de auditoría "antes → después": traduce cada lado con el lookup y,
 *    cuando un lado queda vacío, expresa la acción (asignación / remoción) en vez
 *    de mostrar el valor nulo. El backend envía cadena vacía para el lado sin valor.
 */
export const formatBinnacleLog = (intl: any, binnacleLog: any): string => {
  const entityTypeId = binnacleLog?.entityTypeId;
  const i18nLookup = binnacleLog?.i18NLookup;
  const i18nDescription = binnacleLog?.i18NDescription;
  const binnacleComment = binnacleLog?.comment;

  // Sin metadata de auditoría -> es un comentario/evento, se muestra tal cual
  if (!i18nLookup && !i18nDescription) return binnacleComment;

  // Traduce un lado del cambio; cadena vacía si no hay valor en ese lado
  const translateSide = (raw: string): string => {
    const trimmed = (raw ?? '').trim();
    if (trimmed === '') return '';
    if (!i18nLookup) return trimmed;
    if (entityTypeId === ENTITY_UNITS) {
      // El lookup de unidades se resuelve por i18n; si no hay match se conserva el valor
      return resolveUnitsIntlLookUp(intl, trimmed, i18nLookup) || trimmed;
    }
    // Otras entidades ya traen el valor resuelto desde el backend
    return trimmed;
  };

  const label = i18nDescription ? intl.formatMessage({ id: i18nDescription }) : '';
  const withLabel = (body: string): string => (label ? `${label}: ${body}` : body);

  const parts = String(binnacleComment ?? '').split(' → ');

  if (parts.length === 2) {
    const before = translateSide(parts[0]);
    const after = translateSide(parts[1]);
    const beforeEmpty = before === '';
    const afterEmpty = after === '';

    // Ambos lados con valor -> cambio normal
    if (!beforeEmpty && !afterEmpty) return withLabel(`${before} → ${after}`);

    // Remoción: tenía valor y quedó vacío
    if (!beforeEmpty && afterEmpty) {
      const action = intl.formatMessage({
        id: 'auditEntities.action.removed',
        defaultMessage: 'Removido'
      });
      return label ? `${label} — ${action}: ${before}` : `${action}: ${before}`;
    }

    // Asignación: estaba vacío y ahora tiene valor
    if (beforeEmpty && !afterEmpty) {
      const action = intl.formatMessage({
        id: 'auditEntities.action.assigned',
        defaultMessage: 'Asignado'
      });
      return label ? `${label} — ${action}: ${after}` : `${action}: ${after}`;
    }

    // Ambos vacíos (no debería ocurrir): solo la etiqueta
    return label || (binnacleComment ?? '');
  }

  // Formato inesperado (sin " → "): se conserva el comportamiento previo
  const fallback = i18nLookup
    ? parts.map((p) => translateSide(p) || (p ?? '').trim()).join(' → ')
    : binnacleComment;
  return withLabel(fallback);
};
